<template>
  <div class="chat-analysis">
    <el-alert
      title="聊天触发数据 - 分析请求与市场路由"
      type="info"
      :closable="false"
      show-icon
      class="page-alert"
    >
      <div slot="default">
        支持股票、指数、板块、批次与市场环境请求。市场环境使用后端已识别的
        <strong>market_environment_analysis</strong>，并通过 scope_params_json 透传
        market_fast / market_next_day / market_next_week / pre_market_plan / post_close_review / market_full。
      </div>
    </el-alert>

    <el-card shadow="never" class="toolbar-card">
      <el-row :gutter="12" type="flex" align="middle">
        <el-col :span="5">
          <el-input
            v-model="quickQuery"
            placeholder="输入股票名称/代码"
            size="small"
            clearable
            @keyup.enter.native="handleQuickAnalyze"
          >
            <el-button
              slot="append"
              icon="el-icon-s-promotion"
              :loading="quickAnalyzing"
              @click="handleQuickAnalyze"
            >生成数据包</el-button>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-select v-model="filterTargetType" placeholder="目标类型" size="small" clearable @change="loadHistory(1)">
            <el-option label="股票" value="stock" />
            <el-option label="指数" value="index" />
            <el-option label="板块" value="sector" />
            <el-option label="市场环境" value="market" />
            <el-option label="批次" value="batch_log" />
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-input v-model="filterTargetValue" placeholder="目标值" size="small" clearable @keyup.enter.native="loadHistory(1)" />
        </el-col>
        <el-col :span="4">
          <el-select v-model="filterStatus" placeholder="状态" size="small" clearable @change="loadHistory(1)">
            <el-option label="已创建" value="created" />
            <el-option label="取数中" value="data_fetching" />
            <el-option label="载荷就绪" value="payload_ready" />
            <el-option label="结论已保存" value="conclusion_saved" />
            <el-option label="失败" value="failed" />
            <el-option label="已过期" value="stale" />
          </el-select>
        </el-col>
        <el-col :span="7">
          <el-button type="primary" size="small" icon="el-icon-search" @click="loadHistory(1)">查询</el-button>
          <el-button type="success" size="small" icon="el-icon-plus" @click="openCreateDialog">新建请求</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="loadHistory(1)">刷新</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card shadow="never" class="content-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-files"></i> 分析请求记录</span>
        <span class="header-tip">市场环境请求只创建入口，后端 payload 自动生成映射仍需 P8-L-C 验收。</span>
      </div>

      <el-table :data="historyList" v-loading="historyLoading" stripe size="small" highlight-current-row>
        <el-table-column prop="request_id" label="请求ID" min-width="190" show-overflow-tooltip />
        <el-table-column prop="target_type" label="目标类型" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.target_type === 'market' ? 'warning' : 'info'">{{ getTargetTypeLabel(row.target_type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="target_value" label="目标值" width="140" show-overflow-tooltip />
        <el-table-column prop="intent_type" label="意图" min-width="170" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="mini">{{ getStatusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="165" />
        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="viewFullChain(row.request_id)">链路</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-if="historyTotal > 0"
        :current-page="historyPage"
        :page-size="20"
        :total="historyTotal"
        layout="total, prev, pager, next"
        class="pager"
        @current-change="loadHistory"
      />

      <div v-if="historyList.length === 0 && !historyLoading" class="empty-block">
        <i class="el-icon-document"></i>
        <p>暂无分析请求记录</p>
        <p>可点击“新建请求”创建市场环境请求。</p>
      </div>
    </el-card>

    <el-card v-if="quickResult" shadow="never" class="content-card">
      <div slot="header" class="card-header">
        <span>快捷生成结果</span>
        <el-button type="text" size="small" @click="quickResult = null">关闭</el-button>
      </div>
      <pre>{{ JSON.stringify(quickResult, null, 2) }}</pre>
    </el-card>

    <el-dialog title="新建分析请求" :visible.sync="createDialogVisible" width="640px">
      <el-form :model="createForm" label-width="110px" size="small">
        <el-form-item label="目标类型">
          <el-select v-model="createForm.target_type" style="width:100%" @change="onTargetTypeChange">
            <el-option label="股票" value="stock" />
            <el-option label="指数" value="index" />
            <el-option label="板块" value="sector" />
            <el-option label="市场环境" value="market" />
            <el-option label="批次" value="batch_log" />
            <el-option label="第一本账分组" value="first_book_group" />
            <el-option label="系统自检" value="system_check" />
          </el-select>
        </el-form-item>

        <el-form-item label="意图类型">
          <el-select v-model="createForm.intent_type" style="width:100%">
            <el-option label="单票分析" value="single_stock_analysis" />
            <el-option label="指数分析" value="index_analysis" />
            <el-option label="板块分析" value="sector_analysis" />
            <el-option label="市场环境分析" value="market_environment_analysis" />
            <el-option label="批次分析" value="batch_log_analysis" />
            <el-option label="第一本账分组分析" value="first_book_group_analysis" />
            <el-option label="系统自检" value="system_check" />
          </el-select>
        </el-form-item>

        <el-form-item v-if="createForm.target_type === 'market'" label="市场路由">
          <el-select v-model="createForm.market_mode" style="width:100%">
            <el-option v-for="item in marketRouteModes" :key="item.value" :label="item.label" :value="item.value">
              <span>{{ item.label }}</span>
              <span class="option-desc">{{ item.desc }}</span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item v-if="createForm.target_type === 'market'" label="外围映射">
          <el-switch v-model="createForm.include_global_context" />
          <span class="inline-tip">默认关闭；只有后端 mapping_ready 才能作为弱证据。</span>
        </el-form-item>

        <el-form-item label="目标值">
          <el-input
            v-model="createForm.target_value"
            :disabled="createForm.target_type === 'market'"
            :placeholder="createForm.target_type === 'market' ? 'MARKET.ASHARE' : '如：688347.SH'"
          />
        </el-form-item>

        <el-form-item v-if="createForm.target_type !== 'market'" label="数据范围">
          <el-select v-model="createForm.data_range" style="width:100%">
            <el-option label="1个月" value="1m" />
            <el-option label="3个月" value="3m" />
            <el-option label="6个月" value="6m" />
            <el-option label="1年" value="1y" />
          </el-select>
        </el-form-item>

        <el-alert
          v-if="createForm.target_type === 'market'"
          type="warning"
          :closable="false"
          show-icon
          title="当前前端只创建市场环境分析请求并透传 route mode；后端 REST 自动生成市场 payload 仍需 P8-L-C。"
        />
      </el-form>
      <span slot="footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="handleCreate">创建请求</el-button>
      </span>
    </el-dialog>

    <el-dialog title="全链路查询" :visible.sync="chainDialogVisible" width="900px" top="3vh">
      <div v-loading="chainLoading">
        <pre>{{ JSON.stringify(chainData, null, 2) }}</pre>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { chatAnalysisApi } from '@/api'

const MARKET_TARGET = 'MARKET.ASHARE'

function makeDefaultCreateForm() {
  return {
    target_type: 'stock',
    intent_type: 'single_stock_analysis',
    target_value: '',
    data_range: '3m',
    market_mode: 'market_fast',
    include_global_context: false
  }
}

export default {
  name: 'ChatAnalysis',
  data() {
    return {
      quickQuery: '',
      quickAnalyzing: false,
      quickResult: null,
      filterTargetType: '',
      filterTargetValue: '',
      filterStatus: '',
      historyLoading: false,
      historyList: [],
      historyTotal: 0,
      historyPage: 1,
      createDialogVisible: false,
      creating: false,
      chainDialogVisible: false,
      chainLoading: false,
      chainData: {},
      marketRouteModes: [
        { label: '普通市场快问 / market_fast', value: 'market_fast', desc: 'fast，重组件延迟' },
        { label: '明日市场分析 / market_next_day', value: 'market_next_day', desc: 'full，明日预案' },
        { label: '下周市场预案 / market_next_week', value: 'market_next_week', desc: 'strict，周线/事件窗口' },
        { label: '盘前预案 / pre_market_plan', value: 'pre_market_plan', desc: 'strict，盘前证据' },
        { label: '盘后复盘 / post_close_review', value: 'post_close_review', desc: 'strict，收盘确认' },
        { label: '全证据包 / market_full', value: 'market_full', desc: 'full，全证据' }
      ],
      createForm: makeDefaultCreateForm()
    }
  },
  mounted() {
    this.loadHistory(1)
  },
  methods: {
    async handleQuickAnalyze() {
      if (!this.quickQuery.trim()) {
        this.$message.warning('请输入股票名称或代码')
        return
      }
      this.quickAnalyzing = true
      this.quickResult = null
      try {
        const res = await chatAnalysisApi.autoAnalyze({
          query: this.quickQuery.trim(),
          chat_thread_ref: 'web_quick'
        })
        this.quickResult = res
        if (res && res.success) this.$message.success('分析完成')
        else this.$message.warning((res && res.error) || '分析未完成')
      } catch (e) {
        this.quickResult = {
          success: false,
          error: (e.response && e.response.data && e.response.data.detail) || e.message || '请求失败'
        }
        this.$message.error(this.quickResult.error)
      } finally {
        this.quickAnalyzing = false
      }
    },
    async loadHistory(page) {
      this.historyLoading = true
      this.historyPage = page || this.historyPage
      try {
        const params = { limit: 20, offset: (this.historyPage - 1) * 20 }
        if (this.filterTargetType) params.target_type = this.filterTargetType
        if (this.filterTargetValue) params.target_value = this.filterTargetValue
        if (this.filterStatus) params.status = this.filterStatus
        const res = await chatAnalysisApi.getHistory(params)
        this.historyList = Array.isArray(res.items) ? res.items : []
        this.historyTotal = res.total || 0
      } catch (e) {
        this.$message.error('加载失败')
        this.historyList = []
        this.historyTotal = 0
      } finally {
        this.historyLoading = false
      }
    },
    openCreateDialog() {
      this.createForm = makeDefaultCreateForm()
      this.createDialogVisible = true
    },
    onTargetTypeChange(val) {
      const intentMap = {
        stock: 'single_stock_analysis',
        index: 'index_analysis',
        sector: 'sector_analysis',
        market: 'market_environment_analysis',
        batch_log: 'batch_log_analysis',
        first_book_group: 'first_book_group_analysis',
        system_check: 'system_check'
      }
      this.createForm.intent_type = intentMap[val] || 'single_stock_analysis'
      if (val === 'market') {
        this.createForm.target_value = MARKET_TARGET
        this.createForm.market_mode = this.createForm.market_mode || 'market_fast'
      } else if (this.createForm.target_value === MARKET_TARGET) {
        this.createForm.target_value = ''
      }
    },
    buildScopeParams() {
      if (this.createForm.target_type === 'market') {
        return {
          object_type: 'market',
          target: MARKET_TARGET,
          mode: this.createForm.market_mode,
          route_mode: this.createForm.market_mode,
          include_global_context: !!this.createForm.include_global_context,
          cache_policy: 'reuse_market_same_trade_date',
          source: 'web_admin_market_route'
        }
      }
      return {
        periods: ['daily'],
        range: this.createForm.data_range,
        include_announcement_summary: true
      }
    },
    async handleCreate() {
      const isMarket = this.createForm.target_type === 'market'
      const targetValue = isMarket ? MARKET_TARGET : this.createForm.target_value
      if (!targetValue) {
        this.$message.warning('请输入目标值')
        return
      }
      this.creating = true
      try {
        const res = await chatAnalysisApi.createRequest({
          intent_type: this.createForm.intent_type,
          target_type: this.createForm.target_type,
          target_value: targetValue,
          chat_thread_ref: isMarket ? 'web_admin_market' : 'web_admin',
          scope_params_json: JSON.stringify(this.buildScopeParams())
        })
        this.$message.success((res && res.reused ? '已复用请求：' : '已创建请求：') + (res && res.request_id ? res.request_id : ''))
        this.createDialogVisible = false
        this.loadHistory(1)
      } catch (e) {
        this.$message.error('创建失败：' + ((e.response && e.response.data && e.response.data.detail) || e.message || '未知错误'))
      } finally {
        this.creating = false
      }
    },
    async viewFullChain(requestId) {
      this.chainDialogVisible = true
      this.chainLoading = true
      this.chainData = {}
      try {
        this.chainData = await chatAnalysisApi.getFullChain(requestId)
      } catch (e) {
        this.chainData = { error: (e.response && e.response.data && e.response.data.detail) || e.message || '查询失败' }
        this.$message.error(this.chainData.error)
      } finally {
        this.chainLoading = false
      }
    },
    getTargetTypeLabel(type) {
      const map = { stock: '股票', index: '指数', sector: '板块', market: '市场环境', batch_log: '批次' }
      return map[type] || type
    },
    getStatusTagType(status) {
      const map = { created: 'info', data_fetching: 'warning', payload_ready: 'success', conclusion_saved: '', failed: 'danger', stale: 'info' }
      return map[status] || 'info'
    },
    getStatusLabel(status) {
      const map = { created: '已创建', data_fetching: '取数中', payload_ready: '载荷就绪', conclusion_saved: '结论已保存', failed: '失败', stale: '已过期' }
      return map[status] || status
    }
  }
}
</script>

<style scoped>
.chat-analysis { padding: 0; }
.page-alert { margin-bottom: 16px; }
.toolbar-card { margin-bottom: 0; }
.toolbar-card .el-card__body { padding: 14px 16px; }
.content-card { margin-top: 12px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.header-tip, .inline-tip { color: #909399; font-size: 12px; }
.option-desc { float: right; color: #8492a6; font-size: 12px; }
.pager { margin-top: 12px; text-align: right; }
.empty-block { text-align: center; padding: 40px; color: #909399; }
.empty-block i { font-size: 48px; display: block; margin-bottom: 10px; }
pre { background: #f5f7fa; padding: 10px; border-radius: 4px; max-height: 560px; overflow: auto; font-size: 12px; white-space: pre-wrap; word-break: break-word; }
</style>
